import { useCallback, useEffect, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import {
  renderableNodes,
  isHidden,
  isSubmit,
  labelFor,
  messagesOf,
  textOf,
  redirectTarget,
  responseOf,
  setPath,
} from './flow'
import type { FlowMessage } from './flow'
import type { AnyFlow } from './flows'
import './FlowForm.css'

/**
 * Renders any Ory self-service flow.
 *
 * Login, registration, recovery and verification are the *same* rendering
 * problem: Ory returns a list of UI nodes and the client draws them. Extracting
 * this is what stops four pages each growing their own slightly-different, and
 * therefore slightly-wrong, copy of the four things below — every one of which
 * was learned by watching the real tenant rather than by reading the docs.
 *
 *   1. **Hidden nodes are kept.** `csrf_token` is one; dropping it turns every
 *      submission into a CSRF failure that surfaces as "wrong password".
 *   2. **The submitter's name and value are sent.** That is how Ory tells
 *      "continue with a password" from "continue with a passkey". Without it a
 *      multi-method flow submits as the wrong method.
 *   3. **Both message levels are rendered.** Ory returns "This account does not
 *      exist" as a message on the *identifier node*, not on the flow. Reading
 *      `ui.messages` alone shows the user nothing after they press the button.
 *   4. **HTTP 400 carrying the next flow is the normal path**, not an error —
 *      it is both a validation failure and every step after the first of an
 *      `identifier_first` login. Surfacing it as an exception breaks the flow
 *      at step two.
 */
export interface FlowFormProps {
  readonly flow: AnyFlow
  /**
   * Submit the collected values. Resolves when the flow completed; rejects with
   * whatever the SDK threw, which this component then inspects.
   */
  readonly onSubmit: (body: Record<string, unknown>) => Promise<void>
  /** Called with the next flow when Ory continues or rejects the current one. */
  readonly onContinue: (next: AnyFlow) => void
  /** Ory asked the browser to go somewhere — a social provider, typically. */
  readonly onRedirect: (to: string) => void
  readonly submitLabel?: string
  readonly children?: ReactNode
}

export function FlowForm({
  flow,
  onSubmit,
  onContinue,
  onRedirect,
  submitLabel,
  children,
}: FlowFormProps) {
  const [submitting, setSubmitting] = useState(false)
  const [messages, setMessages] = useState<FlowMessage[]>(() =>
    messagesOf(flow.ui.messages, flow.ui.nodes),
  )

  // Re-derive when the flow itself changes.
  //
  // The initialiser above is lazy, so it runs ONCE, on mount. A parent handing
  // down a new flow — which is how Ory continues recovery and verification —
  // therefore replaced the fields and kept the old (cleared) messages, so the
  // "we have sent you an email" step rendered its inputs and none of its words.
  //
  // Found by writing the first test this component has ever had. The AuthPage
  // fix alone was not enough, and would have looked correct in review (#29).
  useEffect(() => {
    setMessages(messagesOf(flow.ui.messages, flow.ui.nodes))
  }, [flow])

  const submit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (submitting) return
      setSubmitting(true)
      setMessages([])

      const form = new FormData(e.currentTarget)
      const body: Record<string, unknown> = {}
      for (const [k, v] of form.entries()) if (typeof v === 'string') setPath(body, k, v)

      const submitter = (e.nativeEvent as SubmitEvent).submitter
      if (submitter instanceof HTMLButtonElement && submitter.name !== '') {
        setPath(body, submitter.name, submitter.value)
      }

      try {
        await onSubmit(body)
      } catch (err: unknown) {
        const next = await continuationFlow(err)
        if (next !== null) {
          setMessages(messagesOf(next.ui.messages, next.ui.nodes))
          onContinue(next)
        } else {
          const to = await redirectTarget(err)
          if (to !== null) {
            onRedirect(to)
            return
          }
          setMessages([
            {
              id: 0,
              severity: 'error',
              text: err instanceof Error ? err.message : 'Something went wrong. Try again.',
            },
          ])
        }
      } finally {
        setSubmitting(false)
      }
    },
    [onContinue, onRedirect, onSubmit, submitting],
  )

  return (
    <form action={flow.ui.action} method={flow.ui.method} onSubmit={submit} noValidate>
      {messages.length > 0 && (
        <ul className="flowform__messages">
          {messages.map((m, i) => (
            <li
              key={`${m.id}-${i}`}
              className={`flowform__msg flowform__msg--${m.severity}`}
              role={m.severity === 'error' ? 'alert' : 'status'}
            >
              {m.text}
            </li>
          ))}
        </ul>
      )}

      {renderableNodes(flow.ui.nodes).map((n) => {
        switch (n.kind) {
          case 'input': {
            const { key, node, attrs } = n
            if (isHidden(attrs)) {
              return (
                <input
                  key={key}
                  type="hidden"
                  name={attrs.name}
                  defaultValue={String(attrs.value ?? '')}
                />
              )
            }
            if (isSubmit(attrs)) {
              return (
                <button
                  key={key}
                  type="submit"
                  name={attrs.name}
                  value={String(attrs.value ?? '')}
                  className="flowform__submit"
                  disabled={submitting || attrs.disabled === true}
                >
                  {submitting ? 'Working…' : (submitLabel ?? labelFor(node))}
                </button>
              )
            }
            return (
              <label key={key} className="flowform__field">
                <span className="flowform__label">{labelFor(node)}</span>
                <input
                  name={attrs.name}
                  type={attrs.type}
                  defaultValue={String(attrs.value ?? '')}
                  required={attrs.required === true}
                  disabled={attrs.disabled === true}
                  autoComplete={attrs.autocomplete ?? undefined}
                  autoFocus={FOCUS_FIRST.has(attrs.name) || attrs.type === 'password'}
                />
              </label>
            )
          }

          /*
            The QR for a TOTP enrolment. `src` is a data: URI, which the CSP
            rendered by docker-entrypoint.sh already permits (`img-src 'self'
            data:`) — worth stating, because a policy failure here would look
            exactly like this node being dropped again.

            The alt text is Ory's own label where it has one. It is not
            decorative: it is the thing a customer is being asked to scan.
          */
          case 'image':
            return (
              <img
                key={n.key}
                className="flowform__qr"
                src={n.attrs.src}
                alt={labelFor(n.node) || 'Scan this with your authenticator app'}
                width={n.attrs.width}
                height={n.attrs.height}
              />
            )

          /*
            The shared secret, and lookup-secret recovery codes. SELECTABLE, and
            that is the point: a customer whose phone cannot scan needs to type
            it, and a customer given recovery codes needs to copy them somewhere
            safe. Rendering it as plain prose would be a step backwards from
            dropping it, because it would look like commentary.
          */
          case 'text':
            return (
              <p key={n.key} className="flowform__text">
                {labelFor(n.node) !== '' && (
                  <span className="flowform__label">{labelFor(n.node)}</span>
                )}
                <code className="flowform__secret">{textOf(n.attrs)}</code>
              </p>
            )

          case 'anchor':
            return (
              <a key={n.key} className="flowform__anchor" href={n.attrs.href}>
                {n.attrs.title?.text ?? labelFor(n.node)}
              </a>
            )

          /*
            SAID, NOT SWALLOWED. `script` is the live case — Ory uses it for
            WebAuthn — and a customer facing a method this build cannot draw
            should be told, not left looking at a form with a piece missing.
          */
          case 'unsupported':
            return (
              <p key={n.key} className="flowform__unsupported" role="status">
                This sign-in method needs a newer version of this app ({n.nodeType}).
              </p>
            )
        }
      })}

      {children}
    </form>
  )
}

/**
 * Fields that should take focus when a step renders.
 *
 * Multi-step flows redraw the page under the user; losing focus on every step
 * makes a two-step sign-in feel broken.
 */
const FOCUS_FIRST = new Set(['identifier', 'traits.email', 'email', 'code'])

async function continuationFlow(err: unknown): Promise<AnyFlow | null> {
  const res = responseOf(err)
  if (res === null || res.status !== 400) return null
  try {
    const body = (await res.clone().json()) as unknown
    if (typeof body === 'object' && body !== null && 'ui' in body && 'id' in body) {
      return body as AnyFlow
    }
  } catch {
    return null
  }
  return null
}

